import { Fragment, useContext, useRef } from "react";
import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import { SettingsPanel } from "@design-system/SettingsPanel/SettingsPanel.lib";
import { ColumnSettingsTrigger } from "@design-system/ViewToolbar/ColumnSettingsTrigger";
import { DataGridContext } from "../DataGridContext";
import type { DataGridColumnState, DataGridInstance } from "../types";
import {
    getColumnSettingsZones,
    hasColumnChanges,
    resetColumns,
} from "./columnSettingsActions";
import { ColumnSettingsZoneList } from "./ColumnSettingsZone";
import { DensitySetting } from "./DensitySetting";
import { focusPinMenu } from "./focusPinMenu";

const ZONE_TITLES = {
    start: "Pinned left",
    center: "Columns",
    end: "Pinned right",
};

/**
 * The table's column state as values. Widths and variants are not root
 * state, so `instance` alone doesn't change with them.
 */
const useColumnState = <TData extends RowData>(
    instance: DataGridInstance<TData>,
): DataGridColumnState => {
    const { table } = instance;
    const order = useSelector(table.atoms.columnOrder);
    const visibility = useSelector(table.atoms.columnVisibility);
    const pinning = useSelector(table.atoms.columnPinning);
    const sizing = useSelector(table.atoms.columnSizing);
    const variants = useSelector(instance.atoms.columnVariants);
    return { order, visibility, pinning, sizing, variants };
};

const ColumnSettingsPanel = <TData extends RowData>({
    instance,
}: Readonly<{ instance: DataGridInstance<TData> }>) => {
    const { table, defaultColumnState: defaults } = instance;
    const { columnVariants } = instance.atoms;
    const columnState = useColumnState(instance);
    const listRef = useRef<HTMLDivElement>(null);
    const zones = getColumnSettingsZones({ table, columnState }).filter(
        (zone) => zone.rows.length > 0,
    );
    const hasPinnedZone = zones.some((zone) => zone.pin !== undefined);
    const hasChanges = hasColumnChanges({ table, columnState, defaults });

    const handleReset = () => {
        resetColumns({ table, columnVariants, defaults });
        const { defaultDensity, density } = instance;
        if (defaultDensity !== undefined && defaultDensity !== density) {
            instance.optionsRef.current.onDensityChange?.(defaultDensity);
        }
    };

    return (
        <SettingsPanel.Root
            trigger={<ColumnSettingsTrigger hasChanges={hasChanges} />}
            triggerTooltip="Show, hide, pin, and rearrange columns"
            footer={
                hasChanges && (
                    <SettingsPanel.Action onClick={handleReset}>
                        Reset
                    </SettingsPanel.Action>
                )
            }
            scrollContainerRef={listRef}
        >
            {zones.map((zone, zoneIndex) => (
                <Fragment key={zone.pin ?? "center"}>
                    {zoneIndex > 0 && <SettingsPanel.Separator />}
                    <SettingsPanel.Section
                        label={
                            hasPinnedZone && ZONE_TITLES[zone.pin ?? "center"]
                        }
                    >
                        <ColumnSettingsZoneList
                            table={table}
                            zone={zone}
                            scrollContainerRef={listRef}
                            onPinned={(name) =>
                                focusPinMenu({ list: listRef.current, name })
                            }
                        />
                    </SettingsPanel.Section>
                </Fragment>
            ))}
            <DensitySetting instance={instance} />
        </SettingsPanel.Root>
    );
};

/**
 * The column gear: shows, hides, pins, and reorders (drag, or the handle's
 * keyboard mode) the table's value columns, and resets them (with their
 * variants) to
 * `defaultColumnState` (the column definitions unless declared). With
 * `onDensityChange` it also sets the row density. Columns that can't hide
 * (`enableHiding: false`) keep a locked eye. Changes reach the caller
 * through `onColumnStateChange`, so a saved view or localStorage adapter
 * persists them. Inside `<DataGrid>` it finds the table itself; pass
 * `table` to render it elsewhere, such as a page toolbar, where the root's
 * keys don't reach the popup.
 */
export const DataGridColumnSettings = <TData extends RowData>({
    table,
}: Readonly<{ table?: DataGridInstance<TData> }>) => {
    const context = useContext(DataGridContext);
    if (table) {
        return <ColumnSettingsPanel instance={table} />;
    }
    if (!context) {
        throw new Error(
            "DataGrid.ColumnSettings needs a `table` outside <DataGrid>",
        );
    }
    return context.visit((instance) => (
        <ColumnSettingsPanel instance={instance} />
    ));
};
